import { types, getParent } from 'mobx-state-tree';
import { flow } from 'mobx';
import { cameraControl } from '@myplay/all';
import { toast } from "@myplay/ui";

import I18N from '../../I18N';
import { toTitleCase } from '../../utils/stringManipulations';
import { RECORDING, GAME } from '../../utils/constants';

const siteModule = cameraControl.site;
const liveModule = cameraControl.live;
const recordModule = cameraControl.record;
const schedulerModule = cameraControl.scheduler;

const DEFAULT_DURATION = 180 * 60; 

const Team = types
  .model("Team", {
    _id: types.maybeNull(types.string), 
    name: types.maybeNull(types.string)
  })

const Task = types
  .model("Task", {
    _id: types.maybeNull(types.string), 
    startTime: types.maybeNull(types.string), 
    duration: types.maybeNull(types.number),
    team: Team 
  });

const Site = types
  .model("Site",{
    _id: types.maybeNull(types.string), 
    name: types.maybeNull(types.string),
    liveState: types.frozen({}),
    isOnline: types.maybeNull(types.boolean),
    snapshot: types.maybeNull(types.string), 
    isRecording: types.maybeNull(types.boolean), 
    isLiveReady: types.maybeNull(types.boolean), 
    recordingTeam: types.maybeNull(types.string),
    liveBroadcastingTeam: types.maybeNull(types.string),
    recordRoutines: types.maybeNull(types.frozen({})), 
    timeToEndRecord: types.maybeNull(types.number), 
    scheduledTasks: types.maybeNull(types.array(Task))
  });

const SitesStore = types
  .model("Sites", {
    sites: types.array(Site),
    isLoadingSites: true 
  })
  .actions(self => ({
    setSites: flow(function* setSites(sites){ 
      try {
        self.sites = [];
        self.isLoadingSites = true;
        yield Promise.all(
          sites.map(site =>  
            self.setSingleSite(site)
          )
        );
        self.stopLoader();
      } catch (error) {
        console.log(error);
      }
    }),
    stopLoader(){
      self.isLoadingSites = false;
    }, 
    setSingleSite: flow(function* setSingleSite(site){
      try {
        const { TeamsStore } = getParent(self);
        let { team } = site.recordRoutines;
        let { liveState, isRecording, recordEndsOn, recordingTeam } = yield self.checkSite(site);
        recordingTeam = TeamsStore.getTeamNameById(recordingTeam);
        let liveBroadcastingTeam = TeamsStore.getTeamNameById(team);
        if (liveState.isLive) {
          const cameras = yield liveModule.liveAuth(liveState.sessionId);
          liveBroadcastingTeam = TeamsStore.getTeamNameById(cameras[0].team);
        };
        const { time } = yield siteModule.getCurrentTime();
        const localEndingTime = new Date(recordEndsOn).getTime();
        const timeToEndRecord = localEndingTime - time;
        const scheduledTasks = yield schedulerModule.getTasksBySite(site._id);
        const snapshot = yield siteModule.getSiteSnapshot(site._id);
        const updatedSite = { 
          ...site, 
          liveState, 
          isRecording, 
          recordingTeam, 
          timeToEndRecord, 
          scheduledTasks, 
          liveBroadcastingTeam,
          snapshot
        };
        self.addUpdateSite(updatedSite);
      } catch (error) {
        console.log(error);
      }
    }), 
    addUpdateSite(site) {
      const indexOfSite = self.sites.findIndex(s => s._id === site._id);
      if (indexOfSite === -1) {
        const newSites = [...self.sites, site];
        self.sites = [...newSites];
        return;
      }
      self.sites[indexOfSite] = { ...site } ;
    },
    startLive: flow(function* startLive(site, team) {
      try {
        const { UserStore } = getParent(self);
        const { data: { _id } } = UserStore;
        const sessionId = yield liveModule.startLive(
          team._id,
          DEFAULT_DURATION, 
          site.liveState.defaultCameraIndex,
          _id
        );
        const cameras = yield liveModule.liveAuth(sessionId);
        const isLiveReady = yield liveModule.requestCamerasUntilReadyWithCameras(
          cameras
        );
        if (isLiveReady) {
          self.setSiteLiveReady(site._id);
        }
        yield self.setSingleSite(site);
      } catch (error) {
        console.log(error);
      }
    }), 
    stopLive: flow(function* stopLive(site) {
      try {
        const { TeamsStore: { getTeamIdByName }, UserStore: { data: { _id } } } = getParent(self);
        const teamId = getTeamIdByName(site.liveBroadcastingTeam);
        yield liveModule.stopLive(teamId, _id);
        yield self.setSingleSite(site);
      } catch (error) {
        console.log(error);
      }
    }),
    checkSite: flow(function* checkSite(site) {
      try {
        const { liveState, cameras } = yield siteModule.checkSiteBySiteId(site._id);
        let isRecording = false;
        let recordEndsOn = null;
        let recordingTeam = null;
        
        cameras.map(camera => {
          if (!isRecording) {
            isRecording = camera.status === RECORDING;
            recordEndsOn = isRecording ? camera.task.record.recordEndsOn : null;
            recordingTeam = isRecording ? camera.task.record.homeTeam : null;
          }
        });
        return { liveState, isRecording, recordEndsOn, recordingTeam };
      } catch (error) {
        console.log(error);
      }
    }), 
    setSiteLiveReady(siteId) {
      const currentSiteIndex = self.sites.findIndex(site => site._id === siteId); 
      self.sites[currentSiteIndex].isLiveReady = true;
    },
    getSiteById(siteId) {
      return self.sites.filter(site => site._id === siteId)[0];
    }, 
    startRecord: flow(function* startRecord(teamId){
      try {
        const { UserStore } = getParent(self);
        const recordConfig = {
          senderID: UserStore.data._id,
          teamID: teamId, 
          recordType: GAME
        };

        const site = yield recordModule.startRecord(recordConfig);
        yield self.setSingleSite(site);
      } catch (error) {
        console.log(error);
      }
    }), 
    stopRecord: flow(function* stopRecord(teamName){
      try {
        const { TeamsStore: { getTeamIdByName }, UserStore: { data: { _id } } } = getParent(self);
        const teamId = getTeamIdByName(teamName);
        const site = yield recordModule.stopRecord(teamId, _id);

        yield self.setSingleSite(site);
      } catch (error) {
        console.log(error);
      }
    }), 
    scheduleRecord: flow(function* scheduleRecord(params){
      try {
        const { UserStore } = getParent(self);
        const { team, date, duration, title } = params;
        const task = yield schedulerModule.startScheduledRecord({
          senderID: UserStore.data._id,
          teamID: team._id,
          startTime: date,
          duration, 
          title
        });
        self.addTaskToStore(task);
        toast.success(toTitleCase(I18N.t("RECORD_CREATED_SUCCESSFULLY")));
        return true;
      } catch (error) {
        toast.error(toTitleCase(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
        return false;
      }
    }), 
    cancelTask: flow(function* cancelTask(taskId, siteId) {
      try {
        const { UserStore: { data: { _id } } } = getParent(self);
        yield schedulerModule.cancelTask(taskId, _id);
        self.removeTask(taskId, siteId);
        toast.success(toTitleCase(I18N.t("RECORD_DELETED_SUCCESSFULLY")));
      } catch (error) {
        toast.error(toTitleCase(I18N.t("SOMETHING_WENT_WRONG")));
        console.log(error);
      }
    }), 
    removeTask(taskId, siteId) {
      const currentSiteIndex = self.sites.findIndex(site => site._id === siteId);
      const newTaskList = self.sites[currentSiteIndex].scheduledTasks.filter(task => task._id !== taskId);
      self.sites[currentSiteIndex].scheduledTasks = [...newTaskList]; 
    }, 
    addTaskToStore(task) {
      const currentSiteIndex = self.sites.findIndex(site => site._id === task.siteId);
      const indexToPush = self.sites[currentSiteIndex].scheduledTasks.findIndex(currentTask => currentTask.startTime > task.startTime);
      const newTaskList = [...self.sites[currentSiteIndex].scheduledTasks];
      newTaskList.splice(indexToPush,0,task);
      self.sites[currentSiteIndex].scheduledTasks = [...newTaskList];
    }
  }));

export default SitesStore;